import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Send, 
  Image as ImageIcon, 
  X,
  LogOut,
  Trash2,
  Moon,
  Sun,
  ThumbsUp,
  ThumbsDown,
  Menu,
  Plus,
  Shield,
  Check,
  Info
} from 'lucide-react';
import { api } from '../api';
import { fetchWithAuth } from '../api/fetchWithAuth';
import { ThinkingIndicator } from './ThinkingIndicator';
import { Markdown } from './Markdown';
import { SourceLine, type SourceInfo } from './SourceLine';
import { downscaleImage } from '../utils/imageProcessing';
import { POWERED_BY, LINEAGE_FOOTER, APP_VERSION } from '../version';
import type { Chat, Message } from '../types';

interface ExtendedMessage extends Message {
  sources?: SourceInfo[];
  feedback?: 'positive' | 'negative' | null;
  feedbackId?: string;
  feedbackLocked?: boolean;
}

export function ChatPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  
  const [messages, setMessages] = useState<ExtendedMessage[]>([]);
  const [input, setInput] = useState('');
  const [currentChatId, setCurrentChatId] = useState<string | undefined>(chatId);
  const [chats, setChats] = useState<Chat[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [aboutOpen, setAboutOpen] = useState(false);
  const [buildInfo, setBuildInfo] = useState<{ version?: string; build_sha?: string } | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const userEmail = accounts[0]?.username || '';
  const userName = accounts[0]?.name;
  const firstName = (() => {
    if (!userName) return 'there';
    if (userName.includes(',')) {
      const parts = userName.split(',');
      return parts[1]?.trim().split(' ')[0] || parts[0].trim();
    }
    return userName.split(' ')[0];
  })();

  const isUserAdmin = (): boolean => {
    if (accounts.length === 0) return false;
    const account = accounts[0];
    const roles = (account.idTokenClaims as Record<string, unknown>)?.roles as string[] | undefined;
    if (!roles) return false;
    return roles.includes('DevGPT.Admin');
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  useEffect(() => {
    if (userEmail) loadChats();
  }, [userEmail]);

  useEffect(() => {
    if (chatId) {
      setCurrentChatId(chatId);
      loadChat(chatId);
    }
  }, [chatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setIsSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatChatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const loadChats = async () => {
    if (!userEmail) return;
    setChatsLoading(true);
    try {
      const chatList = await api.getChats(userEmail);
      setChats(chatList);
    } catch (error) {
      console.error('Failed to load chats:', error);
    } finally {
      setChatsLoading(false);
    }
  };

  const loadChat = async (id: string) => {
    try {
      const chat = await api.getChat(id);
      const loadedMessages: ExtendedMessage[] = (chat.messages || []).map((msg: ExtendedMessage) => ({
        ...msg,
        feedbackLocked: !!msg.feedback
      }));
      setMessages(loadedMessages);
      setCurrentChatId(id);
      if (window.innerWidth <= 768) setIsSidebarOpen(false);
    } catch (error) {
      console.error('Failed to load chat:', error);
    }
  };

  const handleNewChat = async () => {
    try {
      const { chat_id } = await api.createChat(userEmail);
      setCurrentChatId(chat_id);
      setMessages([]);
      navigate(`/chat/${chat_id}`);
      await loadChats();
      if (window.innerWidth <= 768) setIsSidebarOpen(false);
    } catch (error) {
      console.error('Failed to create chat:', error);
    }
  };

  const handleDeleteChat = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this chat?')) return;
    try {
      await api.deleteChat(id);
      await loadChats();
      if (id === currentChatId) {
        setCurrentChatId(undefined);
        setMessages([]);
        navigate('/chat');
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
    }
  };

  const extractResponseContent = (response: unknown): string => {
    if (typeof response === 'string') return response;
    if (response && typeof response === 'object') {
      const obj = response as Record<string, unknown>;
      if (typeof obj.content === 'string') return obj.content;
      if (typeof obj.text === 'string') return obj.text;
      if (typeof obj.message === 'string') return obj.message;
      if (typeof obj.response === 'string') return obj.response;
      try {
        return JSON.stringify(response, null, 2);
      } catch {
        return '[Unable to display response]';
      }
    }
    return String(response || '');
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || input;
    if (!textToSend.trim() && !selectedImage) return;
    if (isLoading) return;

    const userMessage: ExtendedMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date().toISOString(),
      ...(selectedImage && { image: selectedImage })
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    const imageToSend = selectedImage;
    setSelectedImage(null);
    setIsLoading(true);

    // Add placeholder assistant message with "thinking" state
    const assistantMessage: ExtendedMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      sources: [],
      feedbackLocked: false
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      const response = await api.sendMessage({
        chat_id: currentChatId,
        message: userMessage.content,
        user_email: userEmail,
        image: imageToSend || undefined,
        // D4 — downscale always re-encodes to JPEG, so declare it explicitly
        // rather than letting api.sendMessage default to image/png.
        image_type: imageToSend ? 'image/jpeg' : undefined
      });

      if (response.chat_id && response.chat_id !== currentChatId) {
        setCurrentChatId(response.chat_id);
        window.history.replaceState(null, '', `/chat/${response.chat_id}`);
      }

      const responseContent = extractResponseContent(response.response);
      
      const sources: SourceInfo[] = Array.isArray(response.sources) 
        ? response.sources
            .filter((s: unknown) => s && typeof s === 'object')
            .map((s: unknown) => {
              const src = s as Record<string, unknown>;
              return {
                title: String(src.title || 'Unknown'),
                source: String(src.source || ''),
                relevance: Number(src.relevance || 0),
                metadata_spo_item_weburi: (src.metadata_spo_item_weburi as string) || null
              };
            })
        : [];

      const messageIndex = messages.length;
      const feedbackId = response.feedback_id || `fb-${response.chat_id}-${messageIndex}`;

      setMessages(prev => {
        const updated = [...prev];
        const lastMessage = updated[updated.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          lastMessage.content = responseContent;
          lastMessage.sources = sources;
          lastMessage.feedbackId = feedbackId;
          lastMessage.feedbackLocked = false;
        }
        return updated;
      });

      await loadChats();
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1].content = 'Error: Failed to get response. Please try again.';
        }
        return updated;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = async (messageIndex: number, rating: 'positive' | 'negative') => {
    const message = messages[messageIndex];
    
    if (message?.feedbackLocked || message?.feedback) {
      console.log('Feedback already submitted');
      return;
    }
    
    setMessages(prev => {
      const updated = [...prev];
      if (updated[messageIndex]) {
        updated[messageIndex].feedback = rating;
        updated[messageIndex].feedbackLocked = true;
      }
      return updated;
    });

    try {
      const feedbackId = message?.feedbackId || `fb-${currentChatId}-${messageIndex}`;
      
      const response = await fetchWithAuth(`${import.meta.env.VITE_API_BASE_URL || ''}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback_id: feedbackId,
          chat_id: currentChatId || '',
          message_index: messageIndex,
          rating: rating,
          user_email: userEmail
        })
      });

      const data = await response.json();
      
      if (response.status === 409) {
        console.log('Feedback already exists:', data.existing_rating);
        setMessages(prev => {
          const updated = [...prev];
          if (updated[messageIndex]) {
            updated[messageIndex].feedback = data.existing_rating || rating;
            updated[messageIndex].feedbackLocked = true;
          }
          return updated;
        });
      } else if (!response.ok) {
        throw new Error(data.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setMessages(prev => {
        const updated = [...prev];
        if (updated[messageIndex]) {
          updated[messageIndex].feedback = null;
          updated[messageIndex].feedbackLocked = false;
        }
        return updated;
      });
    }
  };

  // M9 — explicit allowlist + size cap on image input. Catches oversized
  // screenshots before we base64-encode them into a chat payload, and
  // blocks anything that isn't an image mime we actually process.
  const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB
  const ALLOWED_IMAGE_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
  ]);

  const validateImage = (file: File): string | null => {
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      return `Unsupported image type: ${file.type || 'unknown'}. Use PNG, JPEG, GIF, or WebP.`;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      return `Image is ${sizeMB} MB — please use one under ${MAX_IMAGE_BYTES / 1024 / 1024} MB.`;
    }
    if (file.size === 0) {
      return 'Image file is empty.';
    }
    return null;
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateImage(file);
    if (validationError) {
      alert(validationError);
      if (e.target) e.target.value = '';
      return;
    }
    try {
      // D4 — downscale/compress client-side before it enters chat state. Async
      // decode (createImageBitmap) keeps the UI interactive; a 12MP photo lands
      // as a small JPEG instead of a ~10 MB base64 blob that freezes the app.
      const { base64 } = await downscaleImage(file);
      setSelectedImage(base64);
    } catch (error) {
      console.error('Failed to process image:', error);
      alert('Failed to read that image. Please try a different file.');
    }
    // Allow re-selecting the same file later.
    if (e.target) e.target.value = '';
  };

  // B1 FIX: Improved paste handler with better validation and error handling
  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) {
          console.warn('Clipboard image: getAsFile() returned null');
          continue;
        }

        // M9 — same allowlist + size cap as the upload button path.
        const validationError = validateImage(file);
        if (validationError) {
          alert(validationError);
          return;
        }
        
        try {
          // D4 — same client-side downscale as the upload button path. Keeps
          // large pasted screenshots off the freeze path.
          const { base64 } = await downscaleImage(file);

          if (!base64 || base64.length < 100) {
            console.warn('Clipboard image: encoded data too short or empty');
            continue;
          }

          setSelectedImage(base64);
          break; // Successfully processed an image

        } catch (error) {
          console.error('Failed to process pasted image:', error);
          // Show user-friendly error
          alert('Failed to paste image. Please try using the upload button instead.');
        }
        break;
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  // About panel — lazily pull version + build SHA from /api/health (M37 serves
  // both). Fetched once on first open; failures degrade to the static stamp.
  const openAbout = async () => {
    setAboutOpen(true);
    if (buildInfo) return;
    try {
      const health = await api.getHealth();
      setBuildInfo({ version: health.version, build_sha: health.build_sha });
    } catch {
      setBuildInfo({});
    }
  };

  return (
    <div className="chat-container" style={{ display: 'flex', height: 'var(--app-height, 100vh)', overflow: 'hidden' }}>
      {isSidebarOpen && window.innerWidth <= 768 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} onClick={() => setIsSidebarOpen(false)} />
      )}

      <div className="chat-sidebar" style={{ width: isSidebarOpen ? 280 : 0, minWidth: isSidebarOpen ? 280 : 0, background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', transition: 'all 0.3s ease', overflow: 'hidden', position: window.innerWidth <= 768 ? 'fixed' : 'relative', left: 0, top: 0, height: 'var(--app-height, 100vh)', zIndex: 100 }}>
        <div style={{ padding: 'var(--spacing-md)', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>CHAT HISTORY</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-sm)' }}>
          {chatsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--spacing-lg)', color: 'var(--text-secondary)' }}>
              <div className="spinner" style={{ marginRight: 8 }} />Loading...
            </div>
          ) : chats.length === 0 ? (
            <div style={{ padding: 'var(--spacing-md)', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>No conversations yet</div>
          ) : (
            chats.map(chat => (
              <div key={chat.chat_id} className="chat-list-item" style={{ padding: 'var(--spacing-sm)', marginBottom: 'var(--spacing-xs)', borderRadius: 'var(--radius-md)', cursor: 'pointer', background: chat.chat_id === currentChatId ? 'var(--bg-hover)' : 'transparent', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }} onClick={() => { navigate(`/chat/${chat.chat_id}`); loadChat(chat.chat_id); }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="chat-title" style={{ fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>{chat.title || 'New Chat'}</div>
                  <div className="chat-date" style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{formatChatDate(chat.updated_at)}</div>
                </div>
                <button className="btn-icon btn-ghost" onClick={(e) => handleDeleteChat(chat.chat_id, e)} aria-label={`Delete chat: ${chat.title || 'New Chat'}`} style={{ flexShrink: 0, marginLeft: 'var(--spacing-xs)', minHeight: 32, minWidth: 32 }}><Trash2 size={14} /></button>
              </div>
            ))
          )}
        </div>

        <div style={{ padding: 'var(--spacing-md)', borderTop: '1px solid var(--border-color)', flexShrink: 0 }}>
          {isUserAdmin() && (
            <button className="btn-ghost" onClick={() => navigate('/admin')} style={{ width: '100%', justifyContent: 'flex-start', color: 'var(--primary)', fontWeight: 500 }}><Shield size={16} /> Admin Portal</button>
          )}
          <button className="btn-ghost" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} style={{ width: '100%', justifyContent: 'flex-start' }}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>
          <button className="btn-ghost" onClick={handleLogout} style={{ width: '100%', justifyContent: 'flex-start' }}><LogOut size={16} /> Sign Out</button>
          <button className="btn-ghost" onClick={openAbout} style={{ width: '100%', justifyContent: 'flex-start' }}><Info size={16} /> About</button>
          <div style={{ marginTop: 'var(--spacing-xs)', paddingLeft: 'var(--spacing-sm)', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            {LINEAGE_FOOTER}
          </div>
        </div>
      </div>

      {aboutOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="About Doyle Vault"
          onClick={() => setAboutOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 'var(--spacing-md)' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-lg)', maxWidth: 380, width: '100%', boxShadow: 'var(--shadow-lg)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-md)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                <img src="/doyle-logo.png" alt="" style={{ height: 32 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '1.0625rem' }}>Doyle Vault</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Ask</div>
                </div>
              </div>
              <button className="btn-icon btn-ghost" onClick={() => setAboutOpen(false)} aria-label="Close about dialog" style={{ minHeight: 36, minWidth: 36 }}><X size={18} /></button>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
              Grounded answers about the systems Doyle services — every claim tied to a source.
            </p>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: '0.8125rem', margin: 0 }}>
              <dt style={{ color: 'var(--text-muted)' }}>Version</dt>
              <dd style={{ margin: 0, fontFamily: 'monospace' }}>{buildInfo?.version ?? APP_VERSION}</dd>
              <dt style={{ color: 'var(--text-muted)' }}>Build</dt>
              <dd style={{ margin: 0, fontFamily: 'monospace' }}>{buildInfo?.build_sha ?? '—'}</dd>
            </dl>
            <div style={{ marginTop: 'var(--spacing-md)', paddingTop: 'var(--spacing-sm)', borderTop: '1px solid var(--border-color)', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
              {LINEAGE_FOOTER}
            </div>
          </div>
        </div>
      )}

      <div id="main-content" tabIndex={-1} className="chat-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, height: 'var(--app-height, 100vh)' }}>
        <div className="chat-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--spacing-sm) var(--spacing-md)', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexShrink: 0, minHeight: 56 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
            <button className="btn-icon btn-ghost" onClick={() => setIsSidebarOpen(!isSidebarOpen)} aria-label={isSidebarOpen ? 'Close chat history sidebar' : 'Open chat history sidebar'} aria-expanded={isSidebarOpen} style={{ minHeight: 44, minWidth: 44 }}><Menu size={20} /></button>
            <img src="/doyle-logo.png" alt="Doyle Vault" style={{ height: 32 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>Doyle Vault</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{POWERED_BY}</div>
            </div>
          </div>
          <button className="btn-primary" onClick={handleNewChat} style={{ minHeight: 44 }}><Plus size={18} /><span style={{ marginLeft: 4 }}>New Chat</span></button>
        </div>

        <div className="chat-messages" style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-md)', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            {messages.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', textAlign: 'center', padding: 'var(--spacing-lg)' }}>
                <img src="/doyle-logo.png" alt="Doyle Vault" style={{ width: 80, height: 80, marginBottom: 'var(--spacing-md)' }} />
                <h2 style={{ marginBottom: 'var(--spacing-sm)' }}>{getGreeting()}, {firstName}!</h2>
                <p style={{ color: 'var(--text-secondary)', maxWidth: 480 }}>I'm Doyle Vault — your grounded assistant for the systems you service. Ask about panels, vendors, network configs, SOPs, and the institutional knowledge behind them.</p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div key={idx} className="message-row" style={{ marginBottom: 'var(--spacing-lg)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <div className="message-avatar" style={{ width: 36, height: 36, borderRadius: '50%', background: msg.role === 'user' ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, var(--doyle-blue) 0%, var(--doyle-purple) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: msg.role === 'user' ? 'var(--text-primary)' : 'white', fontSize: '0.75rem', fontWeight: 600 }}>
                    {msg.role === 'user' ? 'You' : 'AI'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {msg.image && <img src={`data:image/jpeg;base64,${msg.image}`} alt="Uploaded" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 'var(--radius-md)', marginBottom: 'var(--spacing-sm)' }} />}
                    <div className="message-content" style={{ lineHeight: 1.6, fontSize: '0.9375rem', wordBreak: 'break-word' }}>
                      {msg.content ? (
                        msg.role === 'assistant' ? (
                          <Markdown>{msg.content}</Markdown>
                        ) : <span style={{whiteSpace: 'pre-wrap'}}>{msg.content}</span>
                      ) : (
                        // D2 — animated status line while the single-shot request
                        // is pending. Pool picked from the triggering user message
                        // (the row just above this assistant placeholder).
                        isLoading && idx === messages.length - 1
                          ? <ThinkingIndicator userText={messages[idx - 1]?.content} />
                          : null
                      )}
                    </div>

                    {msg.role === 'assistant' && <SourceLine sources={msg.sources} />}

                    {msg.role === 'assistant' && msg.content && !isLoading && (
                      <div className="feedback-buttons" style={{ marginTop: 'var(--spacing-xs)', display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <button 
                          className="btn-icon btn-ghost feedback-btn" 
                          onClick={() => handleFeedback(idx, 'positive')} 
                          disabled={msg.feedbackLocked || !!msg.feedback}
                          title={msg.feedback === 'positive' ? 'You rated this helpful' : msg.feedbackLocked ? 'Feedback submitted' : 'Mark as helpful'}
                          style={{ 
                            color: msg.feedback === 'positive' ? '#22c55e' : 'var(--text-muted)', 
                            background: msg.feedback === 'positive' ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                            padding: '6px', 
                            minHeight: 32, 
                            minWidth: 32,
                            opacity: (msg.feedbackLocked && msg.feedback !== 'positive') ? 0.3 : 1,
                            cursor: msg.feedbackLocked ? 'default' : 'pointer'
                          }}
                        >
                          <ThumbsUp size={14} />
                        </button>
                        <button 
                          className="btn-icon btn-ghost feedback-btn" 
                          onClick={() => handleFeedback(idx, 'negative')} 
                          disabled={msg.feedbackLocked || !!msg.feedback}
                          title={msg.feedback === 'negative' ? 'You rated this unhelpful' : msg.feedbackLocked ? 'Feedback submitted' : 'Mark as unhelpful'}
                          style={{ 
                            color: msg.feedback === 'negative' ? '#ef4444' : 'var(--text-muted)', 
                            background: msg.feedback === 'negative' ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                            padding: '6px', 
                            minHeight: 32, 
                            minWidth: 32,
                            opacity: (msg.feedbackLocked && msg.feedback !== 'negative') ? 0.3 : 1,
                            cursor: msg.feedbackLocked ? 'default' : 'pointer'
                          }}
                        >
                          <ThumbsDown size={14} />
                        </button>
                        {msg.feedbackLocked && (
                          <span aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem', color: 'var(--text-secondary)', marginLeft: '4px' }}>
                            <Check size={14} /> Recorded
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="chat-input-area" style={{ padding: 'var(--spacing-sm) var(--spacing-md)', paddingBottom: 'calc(var(--spacing-sm) + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--border-color)', background: 'var(--bg-primary)', flexShrink: 0 }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            {selectedImage && (
              <div style={{ marginBottom: 'var(--spacing-sm)', position: 'relative', display: 'inline-block' }}>
                <img src={`data:image/jpeg;base64,${selectedImage}`} alt="Selected" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 'var(--radius-md)' }} />
                <button className="btn-icon btn-danger" onClick={() => setSelectedImage(null)} aria-label="Remove selected image" style={{ position: 'absolute', top: 4, right: 4, minHeight: 24, minWidth: 24, padding: 4 }}><X size={12} /></button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'flex-end' }}>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} style={{ display: 'none' }} />
              <button className="btn-icon btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={isLoading} aria-label="Attach an image" style={{ minHeight: 44, minWidth: 44 }}><ImageIcon size={20} /></button>
              <textarea ref={textareaRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress} onPaste={handlePaste} placeholder="Ask about Vista panels, network configs, or paste/upload a screenshot..." aria-label="Message input. Type a question or paste an image." disabled={isLoading} rows={1} style={{ flex: 1, minHeight: 44, maxHeight: 120, resize: 'none', fontSize: '16px', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }} />
              <button className="btn-primary" onClick={() => handleSendMessage()} disabled={isLoading || (!input.trim() && !selectedImage)} aria-label={isLoading ? 'Sending message, please wait' : 'Send message'} aria-busy={isLoading} style={{ minHeight: 44, minWidth: 44 }}>{isLoading ? <div className="spinner" aria-hidden="true" /> : <Send size={20} />}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}